import { useLanguage } from '@/lib/i18n';
import { motion } from 'framer-motion';
import { RefreshCw, MapPin, GraduationCap, Languages, Heart, FileCheck, Plane } from 'lucide-react';

const DetailedNursesSection = () => {
  const { tr } = useLanguage();

  const jobChangeReasons = [
    { icon: RefreshCw, text: tr('nurses.detailed.jobchange.reason1') || 'Unfaire Konditionen' },
    { icon: RefreshCw, text: tr('nurses.detailed.jobchange.reason2') || 'Überlastung' },
    { icon: RefreshCw, text: tr('nurses.detailed.jobchange.reason3') || 'Ständiges Einspringen' },
    { icon: RefreshCw, text: tr('nurses.detailed.jobchange.reason4') || 'Keine Wertschätzung' },
  ];

  const jobChangeBenefits = [
    { icon: Heart, title: tr('nurses.detailed.jobchange.benefit1.title') || 'Bessere Arbeitsbedingungen', desc: tr('nurses.detailed.jobchange.benefit1.desc') || 'Faire Dienstpläne, angemessene Personalschlüssel und echte Wertschätzung' },
    { icon: MapPin, title: tr('nurses.detailed.jobchange.benefit2.title') || 'Sichere Begleitung', desc: tr('nurses.detailed.jobchange.benefit2.desc') || 'Vom ersten Gespräch bis zum neuen Arbeitsvertrag – diskret und professionell' },
    { icon: GraduationCap, title: tr('nurses.detailed.jobchange.benefit3.title') || 'Karriereperspektiven', desc: tr('nurses.detailed.jobchange.benefit3.desc') || 'Nutzen Sie Ihre Erfahrung für den nächsten Karriereschritt' },
  ];

  const westbalkanBenefits = [
    { icon: GraduationCap, title: tr('nurses.detailed.westbalkan.benefit1.title') || 'Anerkennung & Recht', desc: tr('nurses.detailed.westbalkan.benefit1.desc') || 'Erklärung des Defizitbescheids und der Gleichwertigkeitsprüfung' },
    { icon: Languages, title: tr('nurses.detailed.westbalkan.benefit2.title') || 'Sprachförderung', desc: tr('nurses.detailed.westbalkan.benefit2.desc') || 'B1/B2-Kurse und Partnerschaften mit Sprachschulen' },
    { icon: FileCheck, title: tr('nurses.detailed.westbalkan.benefit3.title') || 'Dokumentenunterstützung', desc: tr('nurses.detailed.westbalkan.benefit3.desc') || 'Übersetzung, Beglaubigung und Behördenkommunikation' },
    { icon: Plane, title: tr('nurses.detailed.westbalkan.benefit4.title') || 'Komplettbetreuung', desc: tr('nurses.detailed.westbalkan.benefit4.desc') || 'Von der Visumsbeantragung bis zur Ankunft in Deutschland' },
  ];

  return (
    <section id="nurses" className="scroll-mt-24 pt-10 md:pt-14 pb-16 md:pb-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-8 md:mb-10"
        >
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-3">
            {tr('nurses.detailed.title') || 'Ihr Weg zu besseren Arbeitsbedingungen'}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">
            {tr('nurses.detailed.subtitle') || 'Ob Sie bereits in Deutschland arbeiten oder aus dem Westbalkan kommen – wir begleiten Sie persönlich'}
          </p>
        </motion.div>

        {/* Für Pflegekräfte in Deutschland (Arbeitgeberwechsel) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10 md:mb-12"
        >
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground">
                  {tr('nurses.detailed.jobchange.title') || 'Sie arbeiten bereits in Deutschland?'}
                </h3>
              </div>

              <p className="text-muted-foreground text-base md:text-lg mb-4 leading-relaxed">
                {tr('nurses.detailed.jobchange.intro') || 'Sie lieben Ihren Beruf, aber die Realität im Pflegealltag ist oft hart. Das muss nicht so bleiben. Wir verstehen Ihre Situation und begleiten Sie auf dem Weg zu einem Arbeitgeber, der Ihre Arbeit schätzt.'}
              </p>

              <button
                type="button"
                onClick={() => {
                  const el = document.querySelector('#contact-form');
                  if (el) {
                    const top = el.getBoundingClientRect().top + window.scrollY - 80;
                    window.scrollTo({ top, behavior: 'smooth' });
                  }
                }}
                className="w-full bg-primary text-primary-foreground rounded-xl p-3.5 md:p-4 border border-primary/30 hover:bg-primary/90 transition-colors text-left mb-5"
              >
                <p className="font-medium text-sm md:text-base">
                  💡 {tr('nurses.detailed.jobchange.hint') || 'Kostenlos & unverbindlich – wir helfen Ihnen dabei, diskret und professionell den nächsten Schritt zu gehen.'}
                </p>
              </button>

              {/* Gründe für Wechsel */}
              <div className="bg-card/50 rounded-xl p-4 md:p-5 mb-5 border border-primary/10">
                <h4 className="font-heading text-base md:text-lg font-semibold text-foreground mb-3">
                  {tr('nurses.detailed.jobchange.reasons.title') || 'Häufige Gründe für einen Wechsel:'}
                </h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {jobChangeReasons.map((reason, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <reason.icon className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-foreground font-medium text-sm md:text-base">{reason.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="grid md:grid-cols-3 gap-4">
                {jobChangeBenefits.map((benefit, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-card rounded-xl p-4 md:p-5 border border-primary/10"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <benefit.icon className="w-4 h-4 text-primary" />
                    </div>
                    <h5 className="font-heading text-sm md:text-base font-semibold text-foreground mb-1.5">{benefit.title}</h5>
                    <p className="text-muted-foreground text-xs md:text-sm leading-relaxed">{benefit.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ...existing code... */}

      </div>
    </section>
  );
};

export default DetailedNursesSection;
